import { ContextProvider } from '@lit/context';
import {
  ReactiveController,
  ReactiveControllerHost,
  ReactiveElement,
} from 'lit';
import debounce from 'lodash-es/debounce';

import { MobileContext } from '@/context';
import { isMobile } from '@/utils';

/**
 * The `MobileController` class implements the `ReactiveController` interface to manage
 * the mobile state of a host element. It provides a context for the mobile state and
 * updates it based on window resize events.
 *
 * The class uses a debounced function to check if the window is in a mobile state and
 * updates the context accordingly. It also adds and removes event listeners for the
 * window resize event when the host is connected and disconnected, respectively.
 */
export class MobileController implements ReactiveController {
  private host: ReactiveControllerHost;
  private contextProvider;

  isMobile: boolean;

  constructor(host: ReactiveControllerHost) {
    (this.host = host).addController(this);
    this.isMobile = isMobile();

    this.contextProvider = new ContextProvider(host as ReactiveElement, {
      context: MobileContext,
      initialValue: this.isMobile,
    });
  }

  _debounceIsMobile = debounce(() => {
    this.isMobile = isMobile();
    this.contextProvider.setValue(this.isMobile);
  }, 250);

  hostConnected() {
    window.addEventListener('resize', this._debounceIsMobile);
  }

  hostDisconnected() {
    window.removeEventListener('resize', this._debounceIsMobile);
  }
}
